// Watchlist preview: saved single markets and arb pairs, seed data and localStorage persistence.

export type Venue = 'kalshi' | 'polymarket';
export type Status = 'open' | 'settled';

export interface Leg {
  venue: Venue;
  ticker: string;
  title: string;
  /** Expected end, epoch ms; null when the venue gave none. */
  expiresAt: number | null;
  status: Status;
}

export interface Item {
  id: string;
  /** Folder segments; empty means unfiled. */
  path: string[];
  name: string;
  legs: Leg[];
  addedAt: number;
}

export const STORE_KEY = 'kg_watchlist_preview_v2';

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Earliest leg's expected end: an arb stops being tradeable when its first leg finishes. */
export function expiryOf(it: Item): number | null {
  const ts = it.legs.map((l) => l.expiresAt).filter((t): t is number => t !== null);
  return ts.length ? Math.min(...ts) : null;
}

export function statusOf(it: Item): Status {
  return it.legs.every((l) => l.status === 'settled') ? 'settled' : 'open';
}

export function splitPath(p: string): string[] {
  return p.split('/').map((s) => s.trim()).filter(Boolean);
}

const K = (ticker: string, title: string, iso: string, status: Status): Leg =>
  ({ venue: 'kalshi', ticker, title, expiresAt: Date.parse(iso), status });
const P = (ticker: string, title: string, iso: string, status: Status): Leg =>
  ({ venue: 'polymarket', ticker, title, expiresAt: Date.parse(iso), status });

/** Real contracts as looked up on 2026-09-26; statuses refresh from the venues once the tab mounts. */
export function seed(now = Date.now()): Item[] {
  const wk2 = ['NFL', '2026 Wk2', 'GB @ MIN'];
  const wk4 = (g: string) => ['NFL', '2026 Wk4', g];
  const rows: Omit<Item, 'id' | 'addedAt'>[] = [
    { path: wk2, name: 'GB moneyline', legs: [
      K('KXNFLGAME-26SEP13GBMIN-GB', 'Green Bay vs Minnesota Pro Football game: Green Bay wins', '2026-09-13T23:25:00Z', 'settled'),
      P('aec-nfl-gb-min-2026-09-13', 'Green Bay Packers vs Minnesota Vikings', '2026-09-13T23:44:47Z', 'settled')] },
    { path: wk2, name: 'Justin Jefferson: 1+ touchdowns', legs: [
      K('KXNFLTD-26SEP13GBMIN-MINJJEFFERSON18-1', 'Justin Jefferson: 1+ touchdowns', '2026-09-14T02:25:00Z', 'settled'),
      P('astatc-nfl-gb-min-2026-09-13-td-jusjef-gte1', 'Justin Jefferson record 1+ touchdowns', '2026-09-13T21:52:45Z', 'settled')] },
    { path: wk2, name: 'Over 45.5 points scored', legs: [
      K('KXNFLTOTAL-26SEP13GBMIN-46', 'Will there be over 45.5 points scored', '2026-09-13T23:25:00Z', 'settled'),
      P('tsc-nfl-gb-min-2026-09-13-total-45pt5', 'Green Bay Packers vs. Minnesota Vikings: over 45.5 points', '2026-09-13T23:24:47Z', 'settled')] },
    { path: wk2, name: 'Justin Jefferson: 90+ receiving yards', legs: [
      K('KXNFLRECYDS-26SEP13GBMIN-MINJJEFFERSON18-90', 'Justin Jefferson: 90+ receiving yards', '2026-09-13T23:25:00Z', 'settled')] },
    { path: wk4('KC @ MIA'), name: 'KC moneyline', legs: [
      K('KXNFLGAME-26SEP27KCMIA-KC', 'Kansas City wins', '2026-09-27T23:00:00Z', 'open'),
      P('aec-nfl-kc-mia-2026-09-27', 'Kansas City Chiefs vs Miami Dolphins', '2026-09-27T20:00:00Z', 'open')] },
    { path: wk4('KC @ MIA'), name: 'Kansas City vs. Miami: over 44.5 points', legs: [
      P('tsc-nfl-kc-mia-2026-09-27-total-44pt5', 'Kansas City vs. Miami: over 44.5 points', '2026-09-27T20:00:00Z', 'open')] },
    { path: wk4('CIN @ PIT'), name: 'CIN moneyline', legs: [
      K('KXNFLGAME-26SEP27CINPIT-CIN', 'Cincinnati wins', '2026-09-27T23:00:00Z', 'open'),
      P('aec-nfl-cin-pit-2026-09-27', 'Cincinnati Bengals vs Pittsburgh Steelers', '2026-09-27T20:00:00Z', 'open')] },
    { path: wk4('CIN @ PIT'), name: 'CIN Bengals vs. PIT Steelers: over 45.5 points', legs: [
      P('tsc-nfl-cin-pit-2026-09-27-total-45pt5', 'CIN Bengals vs. PIT Steelers: over 45.5 points', '2026-09-27T20:00:00Z', 'open')] },
    { path: wk4('LAC @ BUF'), name: 'LAC moneyline', legs: [
      K('KXNFLGAME-26SEP27LACBUF-LAC', 'Los Angeles C wins', '2026-09-27T23:00:00Z', 'open'),
      P('aec-nfl-lac-buf-2026-09-27', 'Los Angeles Chargers vs Buffalo Bills', '2026-09-27T20:00:00Z', 'open')] },
    { path: wk4('LAC @ BUF'), name: 'LA Chargers vs. BUF Bills: over 47.5 points', legs: [
      P('tsc-nfl-lac-buf-2026-09-27-total-47pt5', 'LA Chargers vs. BUF Bills: over 47.5 points', '2026-09-27T20:00:00Z', 'open')] },
  ];
  return rows.map((r, i) => ({ ...r, id: uid(), addedAt: now - (rows.length - i) * 60_000 }));
}

export function load(): Item[] {
  try {
    const v = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    if (Array.isArray(v)) return v as Item[];
  } catch { /* storage blocked or corrupt; fall back to the seed */ }
  return seed();
}

export function save(items: Item[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(items));
  } catch { /* storage blocked */ }
}
